import { practicalStep, type TourStep } from "./runTour"

/**
 * Tutorial práctico de openspec/changes/cccd-team-seedbed-roster-sync y
 * cccd-decouple-payment-from-activation, sobre la ficha de un equipo real
 * (tab "Miembros" primero, luego "Pagos" y "Negociación"). Muestra:
 * - de dónde sale el cupo (categoría del evento) y cómo se ve en el equipo.
 * - que agregar/quitar un miembro ahora sincroniza en el mismo request el
 *   SeedbedGroup tipo 'team' del semillero de esa categoría (sin acción manual).
 * - que confirmar/registrar un pago ya NO activa el equipo — activar es una
 *   decisión aparte, siempre desde "Cambiar estado".
 * Requiere estar en /cccd/teams/:id con la pestaña "Miembros" abierta para
 * que los primeros pasos encuentren sus elementos; los pasos de Pagos/
 * Negociación se saltan solos (runTour) si no están en el DOM porque el
 * admin sigue en otra pestaña — están pensados para correrse tras cambiar
 * de pestaña, no como parte estricta de una sola pasada.
 */
export const cccdTeamRosterSyncTourSteps: TourStep[] = [
  {
    popover: {
      title: "Tutorial práctico: cupo y sincronización de equipo",
      description:
        "Recorre cómo el roster de este equipo CCCD se mantiene igual al grupo del semillero de su categoría, sin que nadie tenga que actualizar los dos a mano — y cómo los pagos dejaron de decidir si el equipo está activo.",
    },
  },
  {
    element: '[data-tour="team-roster-cupo"]',
    popover: {
      title: "Cupo de la categoría",
      description:
        "Este número (\"N / cupo\") viene de maxTeamSize en la categoría del evento (nacional o regional) — se configura una vez ahí y aplica a todos los equipos de esa categoría. Vacío o \"Sin cupo configurado\" significa sin límite.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="team-add-member"]',
    popover: {
      title: "Agregar miembro",
      description:
        "Al agregar a alguien aquí, el servidor valida el cupo (409 si ya está lleno) y, en el mismo request, sincroniza al SeedbedGroup tipo 'team' del semillero de la categoría — como SeedbedEnrollment si ya tiene cuenta, o SeedbedPendingMember si no. Si el cupo está lleno, este buscador se deshabilita con el motivo.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="team-roster-table"]',
    popover: {
      title: 'Quitar un miembro',
      description:
        'La acción "Quitar" de cada fila (antes no existía) borra al CccdTeamMember y también des-inscribe a esa persona del grupo del semillero — best-effort, no falla si nunca se había sincronizado.',
      side: "top",
    },
  },
  practicalStep(
    {
      element: '[data-tour="team-add-member"] input',
      popover: {
        title: "👉 Hazlo tú: busca a alguien",
        description:
          "Escribe un nombre o correo en el buscador real. Verás resultados en vivo — así arranca el flujo de agregar miembro que dispara el sync descrito arriba.",
        side: "bottom",
      },
    },
    "input",
    2,
  ),
  {
    element: '[data-tour="team-payments-tab"]',
    popover: {
      title: "Pagos — solo registro financiero",
      description:
        "Confirmar o registrar un pago aquí (nacional o regional) ya no cambia el estado del equipo. Es puramente un registro financiero — un equipo activo también puede seguir recibiendo pagos.",
      side: "top",
    },
  },
  {
    element: '[data-tour="team-status-actions"]',
    popover: {
      title: "Activar es una decisión aparte",
      description:
        '"Cambiar estado" (pestaña Negociación) es el único camino para activar o desactivar un equipo — es una decisión explícita del admin, no un efecto secundario de un pago. Al activar, este panel sigue disparando la sincronización hacia el semillero.',
      side: "top",
    },
  },
]
